import React, {FunctionComponent, Suspense, useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {BotActionType, BotMode, BotType, IBotAction, IVideo, MessageType} from '@wholesalechange/chatcomponent';
import type {AvatarEndReason, IAvatarDialogueMethods} from '@wholesalechange/videocomponent';
import {FileWithPath} from 'react-dropzone';

import {IStore} from 'redux/interface';
import {dialogueAsyncActions, getDialogueParticipant} from 'redux/pageAsyncActions';
import {avatarApiMethods} from 'redux/api/avatarSession';
import {voiceApiMethods} from 'redux/api/speech';

import Loader from 'components/loader';
import {useChatBotNew} from 'utils/hooks/useChatBotNew';
import {useDictionary} from 'utils/hooks/useDictionary';
import {useLoadFile} from 'utils/hooks/useLoadFile';
import {useUploadImage} from 'utils/hooks/useUploadImage';
import {useUploadVideo} from 'utils/hooks/useUploadVideo';

import {useAvatarDialogueBot} from './useAvatarDialogueBot';

const AvatarVideo = React.lazy(() => import('@wholesalechange/videocomponent').then(module => ({default: module.AvatarVideo})));
const VoiceDialogue = React.lazy(() => import('@wholesalechange/videocomponent').then(module => ({default: module.VoiceDialogue})));

// Same action types the text page forwards to the server.
const forwardedActionTypes = [
  BotActionType.ContentView,
  BotActionType.OpenURL,
  BotActionType.ReturnValue,
  BotActionType.Schedule,
  BotActionType.Image,
  BotActionType.Overlay
];

interface IAvatarDialogue {
  dialogueId: number
  botId: number
  avatarId?: number | null
  apId?: number
  title: string
  // Voice only: the avatar's still image and a spoken conversation, with no video and no provider session.
  voiceOnly?: boolean
  onEnd: (reason: AvatarEndReason) => void
}

export const AvatarDialogue: FunctionComponent<IAvatarDialogue> = ({dialogueId, botId, avatarId, apId, title, voiceOnly, onEnd}: IAvatarDialogue) => {
  const dispatch = useDispatch();
  const chatBot = useChatBotNew(BotType.Dialogue, BotMode.FullHistory, apId);
  const dictionary = useDictionary();
  const {getFileUrlAsync, getVideoUrlAsync, removeFileAsync} = useLoadFile();
  const {uploadImageUrlAsync} = useUploadImage();
  const {uploadVideoAsync} = useUploadVideo();
  const {bot, avatarName, avatarImageUrl, isLoaded: isBotLoaded} = useAvatarDialogueBot(botId, avatarId);

  const {userProfile} = useSelector((state: IStore) => state.auth);
  const {participant} = useSelector((state: IStore) => state.dialogue);

  useEffect(() => {
    chatBot.xpedition.setXpeditionDialogue(dialogueId);
  }, [dialogueId]);

  useEffect(() => {
    if (chatBot.currentDialog && userProfile) {
      dispatch(dialogueAsyncActions[getDialogueParticipant](userProfile.id, chatBot.currentDialog.id));
    }
  }, [userProfile, chatBot.currentDialog]);

  const dialogueMethods: IAvatarDialogueMethods = {
    sendText: (text, {videoCallId, transcribed}) => {
      if (!chatBot.currentDialog || !participant) {
        return;
      }
      chatBot.sendMessage(MessageType.Text, participant, {
        text: text.trim(),
        videoCallId: videoCallId ?? undefined,
        transcribed
      });
    },
    sendAction: (actionButton: IBotAction) => {
      if (!chatBot.currentDialog || !participant || !actionButton || !forwardedActionTypes.includes(actionButton.actionType)) {
        return;
      }
      chatBot.sendMessage(MessageType.Action, participant, {
        button: {
          actionType: actionButton.actionType,
          actionBody: actionButton.actionBody,
          actionUrl: actionButton.actionUrl,
          serialNumber: actionButton.serialNumber,
          text: actionButton.text
        }
      });
    }
  };

  function uploadDialogVideoAsync(file: FileWithPath, type: string): Promise<IVideo> {
    return uploadVideoAsync(file, dialogueId, type);
  }

  function handleLog(event: string, details?: unknown) {
    console.log(voiceOnly ? 'VoiceDialogue' : 'AvatarDialogue', event, details);
  }

  if (!userProfile || !chatBot.currentDialog || !participant || !isBotLoaded) {
    return <Loader nested/>;
  }

  // Both surfaces take the same dialogue, overlay and lifecycle inputs; only the speech API differs.
  const surfaceProps = {
    dialogueId,
    botId,
    avatar: {
      name: avatarName || title,
      imageUrl: avatarImageUrl
    },
    currentUser: userProfile,
    settings: bot?.botSettings?.avatarDialogue,
    messages: chatBot.messages || [],
    dialogueMethods,
    overlayMethods: {
      dictionary,
      getFileUrl: getFileUrlAsync,
      getVideoUrl: getVideoUrlAsync,
      removeFile: removeFileAsync,
      uploadImage: uploadImageUrlAsync,
      uploadVideo: uploadDialogVideoAsync
    },
    callbacks: {onEnd},
    onLog: handleLog
  };

  return <Suspense fallback={<Loader nested/>}>
    {voiceOnly
      ? <VoiceDialogue {...surfaceProps} voiceApiMethods={voiceApiMethods}/>
      : <AvatarVideo {...surfaceProps} avatarApiMethods={avatarApiMethods} loader={<Loader/>}/>}
  </Suspense>;
};
